import React, { useMemo, useState } from "react";
import { LayoutGrid, List, Star } from "lucide-react";
import { FplPitch } from "./FplPitch";
import { ShirtKit } from "./ShirtKit";
import { armbandsFor, splitPoints, starOf, pkey } from "./fplUtils";

const RankedList = ({ xi, points, armbands, star, testid }) => {
  const rows = xi.filter((s) => s.player).sort((a, b) => (points[pkey(b.player)] || 0) - (points[pkey(a.player)] || 0));
  return (
    <div data-testid={`${testid}-list`} className="bg-white text-[#0F172A]">
      <div className="grid grid-cols-[28px_1fr_48px_52px] px-3 py-2 text-[11px] font-bold uppercase text-white" style={{ background: "linear-gradient(90deg,#0F172A,#1E293B)" }}>
        <span>#</span><span>Player</span><span className="text-center">Club</span><span className="text-right">Pts</span>
      </div>
      {rows.map((s, i) => {
        const k = pkey(s.player);
        return (
          <div key={s.id} data-testid={`${testid}-row-${i}`} className={`grid grid-cols-[28px_1fr_48px_52px] items-center px-3 py-1.5 border-b border-[#0F172A]/10 text-sm ${star === k ? "bg-[#FFD700]/25" : ""}`}>
            <span className="text-xs font-bold text-[#0F172A]/60">{i + 1}</span>
            <span className="flex items-center gap-2 min-w-0">
              <ShirtKit short={s.player.short} color={s.player.color} isGK={s.cat === "GK"} className="w-6 h-6 shrink-0" />
              <span className="truncate font-semibold">{s.player.name}</span>
              {armbands[k] && <span className="shrink-0 w-4 h-4 rounded-full bg-[#1a1a1a] text-white text-[9px] font-extrabold flex items-center justify-center">{armbands[k]}</span>}
              {star === k && <Star className="w-3.5 h-3.5 shrink-0 text-[#c9a400] fill-[#FFD700]" />}
            </span>
            <span className="text-center text-xs font-semibold text-[#0F172A]/70">{s.player.short}</span>
            <span className="text-right font-display font-extrabold">{points[k]}</span>
          </div>
        );
      })}
    </div>
  );
};

// Final XI on the FPL pitch (or ranked list) with each player's share of the season points.
export const ResultsPitch = ({ xi = [], total, title = "Your XI", testid = "results-pitch" }) => {
  const [view, setView] = useState("pitch");
  const points = useMemo(() => splitPoints(xi, total), [xi, total]);
  const armbands = useMemo(() => armbandsFor(xi), [xi]);
  const star = starOf(points);
  const starP = xi.find((s) => s.player && pkey(s.player) === star)?.player;
  if (!starP) return null;
  return (
    <div data-testid={testid} className="rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-[#0F172A]" style={{ background: "#10B981" }}>
        <span className="font-display font-extrabold text-sm truncate">{title}</span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="inline-flex rounded-md bg-[#0F172A]/10 p-0.5">
            {[{ k: "pitch", Icon: LayoutGrid }, { k: "list", Icon: List }].map(({ k, Icon }) => (
              <button key={k} data-testid={`${testid}-view-${k}`} onClick={() => setView(k)} aria-label={`${k} view`}
                className={`rounded px-1.5 py-1 transition-colors ${view === k ? "bg-[#0F172A] text-white" : "text-[#0F172A]"}`}>
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </span>
          <span className="text-xs font-bold" data-testid={`${testid}-total`}>{total} pts</span>
        </span>
      </div>
      <div data-testid={`${testid}-star`} className="flex items-center gap-2 px-3 py-1.5 bg-[#FFD700] text-[#0F172A] text-xs font-bold">
        <Star className="w-3.5 h-3.5 fill-[#0F172A]" />
        <span className="uppercase tracking-wide">Player of the Season</span>
        <span className="ml-auto truncate">{starP.name} · {points[star]} pts</span>
      </div>
      {view === "pitch" ? (
        <FplPitch xi={xi} armbands={armbands} points={points} star={star} square={false} testid={`${testid}-pitch`} />
      ) : (
        <RankedList xi={xi} points={points} armbands={armbands} star={star} testid={testid} />
      )}
    </div>
  );
};

export default ResultsPitch;
